import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { api } from '../../api/client'
import type { MedRuolo, MedSistema } from '../../api/types'
import { formatBytes } from '../../attributes'
import { useAuth } from '../../auth/AuthContext'
import { livelloModulo } from '../../auth/permessi'
import { useAsync } from '../../hooks/useAsync'
import { t } from '../../i18n/lingua'
import { Card, ErrorBanner, Spinner, StatCard, formatDateTime } from '../common'
import { DataTable } from '../DataTable'
import { Finestra } from '../ModificheGitlab'
import { MappaClinica } from './MappaClinica'

/**
 * CENSO MED: the clinical systems (PACS, RIS, HL7 engines, reporting
 * workstations) the devices work with. Hosts speaking DICOM/HL7 that are not
 * devices of the area: computers, never electromedical devices for a
 * protocol alone (ADR-0025), shown apart from the devices and their counts.
 * See `med_systems_service`.
 */

/** The link from the system's side: what the device does with it. */
function etichettaLegame(ruolo: MedRuolo | string): string {
  const e: Record<string, string> = {
    pacs: t('Gli manda immagini (DICOM)'),
    dicom_client: t('Gli manda immagini o lo interroga (DICOM)'),
    hl7: t('Messaggi HL7'),
  }
  return e[ruolo] ?? t('Altro traffico')
}

function etichettaFonte(f: string): string {
  return f === 'agent' ? t('agente CENSO') : t('flussi')
}

function Protocolli({ s }: { s: Pick<MedSistema, 'protocols'> }) {
  return <>{s.protocols.map((p) => <span key={p} className="badge badge--info" style={{ marginRight: 4 }}>{p}</span>)}</>
}

function FinestraSistema({ chiave, onChiudi, onApri }: { chiave: string; onChiudi: () => void; onApri: (k: string) => void }) {
  const { principal } = useAuth()
  const vedeCmdb = livelloModulo(principal, 'cis') !== 'hidden'
  const dati = useAsync(() => api.medSystem(chiave), [chiave])
  const s = dati.data
  return (
    <Finestra titolo={s?.name ?? t('Sistema clinico')} sotto={s?.application ?? s?.addresses.join(', ')} onChiudi={onChiudi}>
      <ErrorBanner error={dati.error} />
      {!s ? <Spinner /> : (
        <div className="form-dialog">
          <dl className="kv">
            <dt>{t('Indirizzi')}</dt><dd className="mono">{s.addresses.join(', ')}</dd>
            {s.hostname && s.hostname !== s.name && <><dt>{t('Nome sulla rete')}</dt><dd className="mono">{s.hostname}</dd></>}
            <dt>{t('Che cos’è')}</dt>
            <dd>
              {s.ci_class_label ? t(s.ci_class_label) : t('non in CMDB')}
              {s.ci_id && vedeCmdb && <> · <Link to={`/cis/${s.ci_id}`}>{t('Apri il CI')}</Link></>}
            </dd>
            <dt>{t('Sistema operativo')}</dt>
            <dd>{s.os || '—'}{s.os_candidates && <small className="text-muted"> · {t('può essere {edizioni}', { edizioni: s.os_candidates })}</small>}</dd>
            {s.domain && <><dt>{t('Dominio')}</dt><dd>{s.domain}</dd></>}
            <dt>{t('Applicazione clinica')}</dt><dd>{s.application || s.dicom_implementation || '—'}</dd>
            <dt>{t('Protocolli')}</dt><dd><Protocolli s={s} /></dd>
          </dl>
          {s.dicom_ae.length > 0 && (
            <>
              <h4 className="form__sezione">DICOM</h4>
              <dl className="kv">
                <dt>{t('AE title')}</dt><dd className="mono">{s.dicom_ae.join(', ')}</dd>
                <dt>{t('Implementazione')}</dt><dd className="mono">{s.dicom_implementation || '—'}</dd>
              </dl>
            </>
          )}
          {s.hl7_applications.length > 0 && (
            <>
              <h4 className="form__sezione">HL7</h4>
              <dl className="kv">
                <dt>{t('Applicazioni')}</dt><dd className="mono">{s.hl7_applications.join(', ')}</dd>
                <dt>{t('Struttura (facility)')}</dt><dd className="mono">{s.hl7_facility || '—'}</dd>
              </dl>
            </>
          )}
          {(s.cert_subject || s.http_servers.length > 0) && (
            <>
              <h4 className="form__sezione">{t('Cosa dice di sé')}</h4>
              <dl className="kv">
                {s.cert_subject && <><dt>{t('Certificato')}</dt><dd className="mono">{s.cert_subject}</dd></>}
                {s.http_servers.length > 0 && <><dt>{t('Server web')}</dt><dd className="mono">{s.http_servers.join(' | ')}</dd></>}
              </dl>
            </>
          )}
          <h4 className="form__sezione">{t('Mappa clinica')}</h4>
          <MappaClinica focus={`system:${s.key}`} altezza={360} onApriSistema={onApri} />
          <h4 className="form__sezione">{t('Gli elettromedicali che gli parlano ({n})', { n: s.devices })}</h4>
          {s.connected.length === 0 ? (
            <p className="field__hint">{t('Nessun elettromedicale gli parla nei flussi degli ultimi 7 giorni né nelle conversazioni dell’agente CENSO: il traffico non passa da un router che esporta i flussi, e sul sistema non c’è l’agente.')}</p>
          ) : (
            <DataTable
              id="medical-system-devices"
              rows={s.connected}
              rowKey={(d) => d.id}
              initialSort={{ key: 'bytes', dir: 'desc' }}
              conteggio={{ nome: t('dispositivi') }}
              nomeEsportazione="sistema-clinico-dispositivi"
              columns={[
                { key: 'name', header: t('Dispositivo'), value: (d) => d.name,
                  render: (d) => <Link to={`/medical/devices/${d.id}`}>{d.name}</Link> },
                { key: 'model', header: t('Modello'), value: (d) => [d.vendor, d.model].filter(Boolean).join(' ') },
                { key: 'roles', header: t('Cosa fa'), filter: 'select', value: (d) => d.roles.map(etichettaLegame).join(', ') },
                { key: 'ports', header: t('Porte'), mono: true, value: (d) => d.ports.join(', ') },
                { key: 'sources', header: t('Visto da'), filter: 'select', value: (d) => (d.sources ?? []).map(etichettaFonte).join(' + ') },
                { key: 'bytes', header: t('Traffico'), type: 'number', align: 'right', value: (d) => d.bytes, render: (d) => (d.bytes ? formatBytes(d.bytes) : '—') },
                { key: 'last_seen', header: t('Ultima volta'), type: 'date', value: (d) => d.last_seen ?? '', render: (d) => formatDateTime(d.last_seen) },
              ]}
            />
          )}
        </div>
      )}
    </Finestra>
  )
}

export function VistaSistemi() {
  const dati = useAsync(() => api.medSystems(), [])
  const { principal } = useAuth()
  const scrive = livelloModulo(principal, 'medical') === 'write'
  // «?apri=<key>»: the map opens a system here.
  const [parametri] = useSearchParams()
  const [aperto, setAperto] = useState<string | null>(parametri.get('apri'))
  const [errore, setErrore] = useState<unknown>(null)
  const agenti = dati.data?.agent_conversations ?? true

  async function cambiaAgenti(acceso: boolean) {
    setErrore(null)
    try {
      await api.medSettings({ agent_conversations: acceso })
      await dati.reload()
    } catch (e) {
      setErrore(e)
    }
  }

  const c = dati.data
  if (dati.loading && !c) return <Spinner />
  if (!c) return <ErrorBanner error={dati.error} />
  const conProtocollo = (p: string) => c.systems.filter((s) => s.protocols.includes(p)).length
  return (
    <>
      <p className="field__hint">
        {t('I computer che parlano DICOM o HL7 e non sono elettromedicali: PACS, RIS, motori di integrazione, middleware di laboratorio, postazioni di refertazione. Li riconosce la sonda dal traffico; quanti elettromedicali gli parlano viene dai flussi degli ultimi 7 giorni.')}
        {c.computed_at && ` ${t('Calcolato il {quando}.', { quando: formatDateTime(c.computed_at) })}`}
      </p>
      <div className="checkbox">
        <input id="med-agenti" type="checkbox" checked={agenti} disabled={!scrive || dati.loading}
               onChange={(e) => void cambiaAgenti(e.target.checked)} />
        <label htmlFor="med-agenti">
          {t('Conversazioni dagli agenti CENSO sui sistemi clinici')}
          <small>
            {t('Dove sui sistemi clinici c’è l’agente CENSO, ogni 30 secondi legge la tabella delle connessioni: con chi parla, su quale porta, quale programma. Mai il contenuto: niente immagini, messaggi o dati dei pazienti. Si tengono solo le conversazioni con elettromedicali e sistemi clinici o sulle porte DICOM e HL7, per 30 giorni; le ore in cui un elettromedicale è collegato contano come utilizzo. Vede i monitor che parlano con la centrale nella stessa VLAN, dove i flussi non arrivano.')}
          </small>
        </label>
      </div>
      <ErrorBanner error={errore} />
      <div className="wazuh-kpi">
        <StatCard label={t('Sistemi clinici')} value={c.systems.length} />
        <StatCard label="DICOM" value={conProtocollo('DICOM')} />
        <StatCard label="HL7" value={conProtocollo('HL7')} />
        <StatCard label={t('Non in CMDB')} value={c.not_in_cmdb} tone={c.not_in_cmdb ? 'warn' : 'ok'}
                  hint={t('visti sul filo, non ancora registrati')} />
      </div>
      <Card title={t('Sistemi clinici')}>
        <DataTable
          id="medical-systems"
          rows={c.systems}
          rowKey={(s) => s.key}
          initialSort={{ key: 'devices', dir: 'desc' }}
          conteggio={{ nome: t('sistemi') }}
          nomeEsportazione="sistemi-clinici"
          onRowClick={(s) => setAperto(s.key)}
          columns={[
            { key: 'name', header: t('Nome'), value: (s) => s.name,
              render: (s) => (
                <>
                  <button type="button" className="link" onClick={() => setAperto(s.key)}>{s.name}</button>
                  {s.agent && <span className="badge badge--ok" style={{ marginLeft: 6 }} title={t('L’agente CENSO su questo sistema riporta le sue conversazioni')}>{t('agente')}</span>}
                </>
              ) },
            { key: 'addresses', header: t('Indirizzi'), mono: true, value: (s) => s.addresses.join(', ') },
            { key: 'class', header: t('Che cos’è'), filter: 'select',
              value: (s) => (s.ci_class_label ? t(s.ci_class_label) : t('non in CMDB')) },
            { key: 'application', header: t('Applicazione clinica'), value: (s) => s.application || s.dicom_implementation || '' },
            { key: 'protocols', header: t('Protocolli'), filter: 'select', value: (s) => s.protocols.join(' + '),
              render: (s) => <Protocolli s={s} /> },
            { key: 'dicom_ae', header: t('AE title'), mono: true, value: (s) => s.dicom_ae.join(', '), hidden: true },
            { key: 'hl7', header: t('Applicazioni HL7'), mono: true, value: (s) => s.hl7_applications.join(', '), hidden: true },
            { key: 'os', header: t('Sistema operativo'), value: (s) => s.os ?? '', hidden: true },
            { key: 'devices', header: t('Elettromedicali collegati'), type: 'number', align: 'right', value: (s) => s.devices },
            { key: 'last_seen', header: t('Ultima volta sul filo'), type: 'date', value: (s) => s.last_seen ?? '', render: (s) => formatDateTime(s.last_seen) },
          ]}
        />
      </Card>
      {aperto && <FinestraSistema key={aperto} chiave={aperto} onChiudi={() => setAperto(null)} onApri={setAperto} />}
    </>
  )
}
